/* eslint-disable @typescript-eslint/no-explicit-any */
import { Image, Popover, Space, Spin, Tag, Typography } from "antd";
import { useEffect, useState } from "react";

// --- Interface Definitions (for clarity and type safety) ---
interface WeatherCondition {
  text: string;
  icon: string;
  code: number;
}

interface DayForecast {
  maxtemp_c: number;
  maxtemp_f: number;
  mintemp_c: number;
  mintemp_f: number;
  avgtemp_c: number;
  avgtemp_f: number;
  condition: WeatherCondition;
  daily_will_it_rain: number;
  daily_chance_of_rain: number;
  daily_will_it_snow: number;
  daily_chance_of_snow: number;
  maxwind_kph: number; // Max wind speed in kilometers per hour
  avghumidity: number; // Average humidity as a percentage
}

interface ForecastDay {
  date: string;
  date_epoch: number;
  day: DayForecast;
  // Add other properties if they exist in your API response and you need them
  astro: any; // e.g., sunrise, sunset data
  hour: any[]; // hourly forecast data
}

interface Forecast {
  forecastday: ForecastDay[];
}

interface Location {
  name: string;
  region: string;
  country: string;
  lat: number;
  lon: number;
  tz_id: string;
  localtime_epoch: number;
  localtime: string;
}

interface WeatherApiResponse {
  location: Location;
  current: any; // Current weather data, can be more specific if needed
  forecast?: Forecast; // Forecast data, contains forecastday array
}

// --- WeatherWrapper Component ---
export default function WeatherWrapper() {
  const [weather, setWeather] = useState<{
    high: number;
    low: number;
    icon: string;
    precipitationChance?: number;
    windKph?: number;
    humidity?: number;
  } | null>(null);

  const fetchWeatherData = async () => {
    // IMPORTANT: Replace with your actual WeatherAPI key and desired location coordinates
    const API_KEY = "d9bd73568247491c88e153434252307"; // Your WeatherAPI key
    const LOCATION_QUERY = "Dallas"; // Example: Dallas City coordinates (lat,lng)

    // API endpoint for 1-day forecast (q=location, days=1)
    const API_ENDPOINT = `https://api.weatherapi.com/v1/forecast.json?key=${API_KEY}&q=${LOCATION_QUERY}&days=1`;

    try {
      const response = await fetch(API_ENDPOINT);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: WeatherApiResponse = await response.json();
      // console.log("Weather data:", data);

      // Ensure forecast and forecastday exist before accessing
      const todayForecast = data.forecast?.forecastday?.[0]?.day;

      if (todayForecast) {
        const highTemp = todayForecast.maxtemp_f; // Using Fahrenheit as per image
        const lowTemp = todayForecast.mintemp_f;
        const weatherIcon = todayForecast.condition?.icon;
        const precipitationChance = todayForecast.daily_chance_of_rain;
        const windKph = todayForecast.maxwind_kph;
        const humidity = todayForecast.avghumidity;

        setWeather({
          high: highTemp,
          low: lowTemp,
          icon: weatherIcon,
          precipitationChance,
          windKph,
          humidity,
        });
      } else {
        console.warn("No forecast data available for today.");
        setWeather(null);
      }
    } catch (error) {
      console.error("Failed to fetch weather data:", error);
      setWeather(null);
    }
  };

  // Fetch weather data when the component mounts
  useEffect(() => {
    fetchWeatherData();
  }, []); // Empty dependency array means this runs once on mount

  const weatherDetailsContent = (
    <div>
      <Typography.Title level={4} style={{ margin: "0 0 4px 0" }}>
        Weather details
      </Typography.Title>
      {weather ? (
        <Space direction="vertical" size={12}>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Image
              src={weather.icon}
              alt="Weather Icon"
              width={32}
              preview={false}
            />
            <Tag className="default_tag">
              Highest:{" "}
              <span style={{ fontWeight: "600", color: "#F06400" }}>
                {Math.round(weather.high)}°
              </span>
            </Tag>
            <Tag className="default_tag">
              Lowest:{" "}
              <span style={{ fontWeight: "600", color: "#2098EE" }}>
                {Math.round(weather.low)}°
              </span>
            </Tag>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "90px" }}>
              <Typography.Text
                style={{
                  fontWeight: "500",
                  fontSize: "12px",
                  color: "#151718",
                }}
              >
                Precipitation
              </Typography.Text>
              <br />
              <div className="detailsData">
                <Image
                  src="/images/schedule/precipitation.svg"
                  preview={false}
                />
                <Typography.Text>
                  {weather.precipitationChance
                    ? Math.round(weather.precipitationChance)
                    : 0}{" "}
                  <span>%</span>
                </Typography.Text>
              </div>
            </div>
            <div style={{ width: "90px" }}>
              <Typography.Text
                style={{
                  fontWeight: "500",
                  fontSize: "12px",
                  color: "#151718",
                }}
              >
                Wind
              </Typography.Text>
              <br />
              <div className="detailsData">
                <Image src="/images/schedule/windy.svg" preview={false} />
                <Typography.Text>
                  {weather.windKph ? Math.round(weather.windKph) : 0}{" "}
                  <span>km/h</span>
                </Typography.Text>
              </div>
            </div>
            <div style={{ width: "90px" }}>
              <Typography.Text
                style={{
                  fontWeight: "500",
                  fontSize: "12px",
                  color: "#151718",
                }}
              >
                Humidity
              </Typography.Text>
              <br />
              <div className="detailsData">
                <Image src="/images/schedule/humidity.svg" preview={false} />
                <Typography.Text>
                  {weather.humidity ? Math.round(weather.humidity) : 0}
                  <span>%</span>
                </Typography.Text>
              </div>
            </div>
          </div>
        </Space>
      ) : (
        <Typography.Text type="secondary">
          No detailed weather data available.
        </Typography.Text>
      )}
    </div>
  );

  return (
    <div className="weatherWrapper">
      {weather ? (
        <Popover
          arrow={false}
          content={weatherDetailsContent}
          placement="bottomRight"
          overlayClassName="weatherPopoverOpen"
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div className="weatherDetails">
              H: <Typography.Text>{Math.round(weather.high)}°</Typography.Text>
            </div>
            <div className="weatherDetails">
              L: <Typography.Text>{Math.round(weather.low)}°</Typography.Text>
            </div>
            {weather.icon && (
              <Image
                src={weather.icon}
                alt="Weather Icon"
                width={32}
                height={32}
                preview={false}
                fallback="https://placehold.co/32x32/E0E0E0/000000?text=NA" // Fallback image
              />
            )}
          </div>
        </Popover>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "32px",
            justifyContent: "center",
          }}
        >
          <Spin size="small" />
        </div>
      )}
    </div>
  );
}
